import React, {
    useState,
    useEffect,
} from "react";
import type {
    DataModel,
    MainTab,
    MetricSection,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    ReportFolder,
} from "../types";
import {
    buildReportConfigurationPayload,
} from "../reportConfigurationPersistence";

export const stableJson = (value: unknown): string => {
    if (!value || typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map((item) => stableJson(item)).join(",")}]`;
    return `{${Object.keys(value as Record<string, unknown>).sort().map((key) => `${JSON.stringify(key)}:${stableJson((value as Record<string, unknown>)[key])}`).join(",")}}`;
};

type ConfigurationChangeTrackingInputs = {
    plans: PlanConfig[];
    commitPlanNavigation: (planId: string) => void;
    discardCurrentReport: () => void;
    dataModels: Record<string, DataModel>;
    lookupOptionsByKey: Record<string, ReportConfigurationLookupOption[]>;
    mainTab: MainTab;
    selectedFolderId: string;
    setSelectedFolderId: React.Dispatch<React.SetStateAction<string>>;
    selectedReportId: string;
    setSelectedReportId: React.Dispatch<React.SetStateAction<string>>;
    setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
    configSaveLoading: boolean;
    savedReportBaselines: Record<string, { plan: PlanConfig; folder: ReportFolder; report: ReportConfig; }>;
    selectedPlan: PlanConfig;
    selectedFolder: ReportFolder;
    selectedReport: ReportConfig | undefined;
    lookups: Record<string, string[]>;
    saveCurrent: () => Promise<boolean>;
};

export const useConfigurationChangeTracking = ({
    plans,
    commitPlanNavigation,
    discardCurrentReport,
    dataModels,
    lookupOptionsByKey,
    mainTab,
    selectedFolderId,
    setSelectedFolderId,
    selectedReportId,
    setSelectedReportId,
    setExpandedFolders,
    configSaveLoading,
    savedReportBaselines,
    selectedPlan,
    selectedFolder,
    selectedReport,
    lookups,
    saveCurrent,
}: ConfigurationChangeTrackingInputs) => {
    const [pendingReportNavigation, setPendingReportNavigation] = useState<null | { folderId: string; reportId: string; folderLabel: string; reportLabel: string }>(null);
    const [pendingPlanNavigation, setPendingPlanNavigation] = useState<null | { planId: string; planLabel: string }>(null);
    const stripSnapshotNoise = (value: unknown): unknown => {
        if (!value || typeof value !== "object") return value;
        if (Array.isArray(value)) return value.map(stripSnapshotNoise);

        return Object.keys(value as Record<string, unknown>).reduce((acc, key) => {
            if (key === "logicalId" || key === "metricLogicalId") return acc;
            acc[key] = stripSnapshotNoise((value as Record<string, unknown>)[key]);
            return acc;
        }, {} as Record<string, unknown>);
    };
    const reportSnapshot = (plan: PlanConfig, folder: ReportFolder, report: ReportConfig) => {
        const built = buildReportConfigurationPayload({
            plan,
            folder,
            report,
            dataModels,
            lookupOptionsByKey,
            lookupValues: lookups,
        });
        const configJson = built.payload.configJson as {
            report: ReportConfig;
            generatedQueries: unknown[];
        };
        const comparableReport = {
            ...configJson.report,
            sections: (configJson.report.sections || []).map((section: MetricSection) => {
                if (!section.excelLayout) return section;
                const { saveAsTemplate, templateSaveMode, sourceTemplateName, sourceTemplateIsGlobal, ...excelLayout } = section.excelLayout;
                return {
        ...section,
        excelLayout,
    };
            }),
        };

        return stableJson({
            report: stripSnapshotNoise(comparableReport),
            generatedQueries: stripSnapshotNoise(configJson.generatedQueries),
        });
    };

    const selectedReportBaseline = selectedReportId ? savedReportBaselines[selectedReportId] : null;
    const currentReportSnapshot = selectedFolder && selectedReport
        ? reportSnapshot(selectedPlan, selectedFolder, selectedReport)
        : "";
    const baselineReportSnapshot = selectedReportBaseline
        ? reportSnapshot(selectedReportBaseline.plan, selectedReportBaseline.folder, selectedReportBaseline.report)
        : "";
    const hasUnsavedReportConfigurationChanges = !!currentReportSnapshot
        && !!baselineReportSnapshot
        && currentReportSnapshot !== baselineReportSnapshot;
    const pendingExcelLayoutTemplateSections = selectedReport?.sections.filter((section) => !!section.excelLayout?.saveAsTemplate) || [];
    const hasPendingExcelLayoutTemplateSave = pendingExcelLayoutTemplateSections.length > 0;
    const hasPendingExcelLayoutTemplateUpdate = pendingExcelLayoutTemplateSections.some((section) => section.excelLayout?.templateSaveMode === "update");
    const hasPendingExcelLayoutTemplateCreate = pendingExcelLayoutTemplateSections.some((section) => section.excelLayout?.templateSaveMode !== "update");
    const hasReportChangesToSave = hasUnsavedReportConfigurationChanges || hasPendingExcelLayoutTemplateSave;
    useEffect(() => {
        if (!hasReportChangesToSave) return;
        const confirmBeforeRefresh = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", confirmBeforeRefresh);
        return () => window.removeEventListener("beforeunload", confirmBeforeRefresh);
    }, [hasReportChangesToSave]);

    const reportBaselinePending = mainTab === "reports" && !!selectedReport && !selectedReportBaseline;
    const noReportChangesToSave = mainTab === "reports"
        && !!selectedReport
        && !!selectedReportBaseline
        && !hasReportChangesToSave;

    const commitReportNavigation = (folderId: string, reportId: string) => {
        setSelectedFolderId(folderId);
        setSelectedReportId(reportId);
        setExpandedFolders(new Set(folderId ? [folderId] : []));
    };

    const requestReportNavigation = (folderId: string, reportId: string) => {
        if (folderId === selectedFolderId && reportId === selectedReportId) return;

        const folder = selectedPlan.reportFolders.find((item) => item.id === folderId);
        const report = folder?.reports.find((item) => item.id === reportId);

        if (!hasReportChangesToSave) {
            commitReportNavigation(folderId, reportId);
            return;
        }

        setPendingReportNavigation({
            folderId,
            reportId,
            folderLabel: folder?.label || "Reports",
            reportLabel: report?.label || "No report selected",
        });
    };

    const discardReportNavigation = () => {
        if (!pendingReportNavigation || configSaveLoading) return;

        const nextNavigation = pendingReportNavigation;
        discardCurrentReport();
        setPendingReportNavigation(null);
        commitReportNavigation(nextNavigation.folderId, nextNavigation.reportId);
    };

    const saveAndContinueReportNavigation = async () => {
        if (!pendingReportNavigation || configSaveLoading) return;

        const nextNavigation = pendingReportNavigation;
        const saved = await saveCurrent();
        if (!saved) return;

        setPendingReportNavigation(null);
        commitReportNavigation(nextNavigation.folderId, nextNavigation.reportId);
    };
    const requestPlanNavigation = (planId: string) => {
        if (planId === selectedPlan.id || configSaveLoading) return;
        const plan = plans.find((item) => item.id === planId);
        if (!plan) return;
        if (!hasReportChangesToSave) {
            commitPlanNavigation(planId);
            return;
        }
        setPendingPlanNavigation({ planId, planLabel: plan.clientName || plan.name || "Selected Health Plan" });
    };

    const discardPlanNavigation = () => {
        if (!pendingPlanNavigation || configSaveLoading) return;
        const nextNavigation = pendingPlanNavigation;
        discardCurrentReport();
        setPendingPlanNavigation(null);
        commitPlanNavigation(nextNavigation.planId);
    };

    const saveAndContinuePlanNavigation = async () => {
        if (!pendingPlanNavigation || configSaveLoading) return;
        const nextNavigation = pendingPlanNavigation;
        const saved = await saveCurrent();
        if (!saved) return;
        setPendingPlanNavigation(null);
        commitPlanNavigation(nextNavigation.planId);
    };

    return {
        pendingPlanNavigation,
        setPendingPlanNavigation,
        requestPlanNavigation,
        discardPlanNavigation,
        saveAndContinuePlanNavigation,
        pendingReportNavigation,
        setPendingReportNavigation,
        hasPendingExcelLayoutTemplateUpdate,
        hasPendingExcelLayoutTemplateCreate,
        reportBaselinePending,
        noReportChangesToSave,
        requestReportNavigation,
        discardReportNavigation,
        saveAndContinueReportNavigation,
    };
};
